import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { FormWrapper } from "@amzn/stencil-react-components/form";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, GridContainer, GridItem, Row } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { TextArea } from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useAIForMeData } from "hooks/useAIForMeData";
import { ApprovalStatus } from "lib/models/aiforme-models";
import _ from "lodash";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import { useMessageBannersBox } from "../MessageBannersBox/hook";
import {
  AiForMeFormData,
  BasePlatform,
  BasePlatformMetadataTaxonomyId,
  BINDLE_PATTERN,
  PlatformName,
  TOOL_MATURITY_OPTIONS,
  ToolMaturity,
} from "./constants/platform-constants";
import { detectPlatform, getDefaultDataClassification, PlatformDetectionResult } from "./helpers";
import { getPublicDataClassificationValue } from "./helpers/data-classification-helper";
import { editTool, submitTool } from "./helpers/tool-submission-helper";
import { MultiSelect } from "./MultiSelect";

export interface AiForMeIntakeFormProps {
  onNext: (formData: AiForMeFormData) => void;
  initialData?: AiForMeFormData | null;
  isEdit: boolean;
}

export const AiForMeIntakeForm: FC<AiForMeIntakeFormProps> = (props: AiForMeIntakeFormProps) => {
  const history = useHistory();

  const { categories, jobFamilies, dataClassifications, loadingAIForMe } = useAIForMeData();
  const defaultPublicClassification = useMemo(
    () => getPublicDataClassificationValue(dataClassifications || []),
    [dataClassifications]
  );

  const { addErrorBanner, addMessageBanner } = useMessageBannersBox();

  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [directToolUrl, setDirectToolUrl] = useState<string>("");
  const [documentationUrl, setDocumentationUrl] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedJobFamilies, setSelectedJobFamilies] = useState<string[]>([]);
  const [toolMaturity, setToolMaturity] = useState<ToolMaturity>(ToolMaturity.DEVELOPMENT);
  const [dataClassification, setDataClassification] = useState<string>(defaultPublicClassification);
  const [basePlatform, setBasePlatform] = useState<BasePlatform>(BasePlatform.OTHER);
  const [platformName, setPlatformName] = useState<PlatformName>(PlatformName.MODEL_HOSTING);
  const [tags, setTags] = useState<string[]>([]);
  const [toolBindle, setToolBindle] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [basePlatformMetadataTaxonomyId, setBasePlatformMetadataTaxonomyId] = useState<
    BasePlatformMetadataTaxonomyId | undefined
  >(undefined);

  // URL analysis state
  const [urlAnalysis, setUrlAnalysis] = useState<PlatformDetectionResult | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDirectToolUrlChange = useCallback((e: any) => {
    const url = e.target.value;
    setDirectToolUrl(url);

    if (url.trim()) {
      const analysis = detectPlatform(url.trim());
      setUrlAnalysis(analysis);
      setBasePlatform(analysis.basePlatform);
      setPlatformName(analysis.platformName);
      setBasePlatformMetadataTaxonomyId(analysis.basePlatformMetadataTaxonomyId);
      setToolMaturity(
        analysis.platformName === PlatformName.THIRD_PARTY ? ToolMaturity.PRODUCTION : ToolMaturity.DEVELOPMENT
      );
    } else {
      setUrlAnalysis(null);
      setBasePlatform(BasePlatform.OTHER);
      setPlatformName(PlatformName.THIRD_PARTY);
      setBasePlatformMetadataTaxonomyId(undefined);
    }
  }, []);

  const isDataClassificationDisabled = (): boolean => {
    return toolMaturity === ToolMaturity.DEVELOPMENT || toolMaturity === ToolMaturity.BETA;
  };

  const showBindleInput = (): boolean => {
    if (!urlAnalysis) return false;
    return (
      urlAnalysis.platformName != PlatformName.THIRD_PARTY && urlAnalysis.platformName != PlatformName.MODEL_HOSTING
    );
  };

  const handleToolMaturityChange = useCallback(
    (maturity: string) => {
      const toolMaturity = maturity as ToolMaturity;
      setToolMaturity(toolMaturity);

      if (toolMaturity === ToolMaturity.DEVELOPMENT || toolMaturity === ToolMaturity.BETA) {
        setDataClassification(defaultPublicClassification);
      } else {
        setDataClassification(getDefaultDataClassification(urlAnalysis, defaultPublicClassification));
      }
    },
    [urlAnalysis, defaultPublicClassification]
  );

  // Update data classification when URL analysis changes
  useEffect(() => {
    if (urlAnalysis) {
      if (toolMaturity === ToolMaturity.DEVELOPMENT || toolMaturity === ToolMaturity.BETA) {
        setDataClassification(defaultPublicClassification);
      } else {
        setDataClassification(getDefaultDataClassification(urlAnalysis, defaultPublicClassification));
      }
    }
  }, [urlAnalysis, toolMaturity, defaultPublicClassification]);

  // populating form with initial data when provided from a user clicking back
  useEffect(() => {
    if (props.initialData) {
      const data = props.initialData;
      setTitle(data.title || "");
      setDescription(data.description || "");
      setDirectToolUrl(data.directToolUrl || "");
      setDocumentationUrl(data.documentationUrl || "");
      setSelectedCategories(data.selectedCategories || []);
      setSelectedJobFamilies(data.selectedJobFamilies || []);
      setToolMaturity(data.toolMaturity || ToolMaturity.DEVELOPMENT);
      setDataClassification(data.dataClassification || defaultPublicClassification);
      setBasePlatform(data.basePlatform || BasePlatform.OTHER);
      setPlatformName(data.platformName || PlatformName.MODEL_HOSTING);
      setUrlAnalysis(data.urlAnalysis || null);
      setBasePlatformMetadataTaxonomyId(data.basePlatformMetadataTaxonomyId);
      setTags(data.tags || []);
    }
  }, [props.initialData]);

  const handleToolSubmission = async (formData: AiForMeFormData, taxonomyId?: string) => {
    const callbacks = {
      addErrorBanner,
      addMessageBanner,
      navigateToSuccess: () => history.push(props.isEdit ? Paths.aiformeMyTools.url : Paths.aiforme.url),
      setIsLoading,
    };

    if (props.isEdit) {
      if (!taxonomyId) {
        addErrorBanner(getString("aiForMe.missingData"));
        return;
      }
      // Add edit-specific data to formData
      const editFormData = {
        ...formData,
        status: props.initialData?.status,
        creationDate: props.initialData?.creationDate,
        catalogicalGeneratedVersion: props.initialData?.catalogicalGeneratedVersion,
      };
      await editTool(editFormData, taxonomyId, callbacks);
    } else {
      await submitTool(formData, callbacks);
    }
  };

  // Helper function to determine if we should show Next button (for custom built and third party) or Submit Tool button
  const shouldShowNextButton = (): boolean => {
    return platformName === PlatformName.MODEL_HOSTING || platformName === PlatformName.THIRD_PARTY; // Custom Built or Third Party
  };

  const handleSubmit = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();

    const trimmedTitle = title.trim();

    const formData = {
      title: trimmedTitle,
      description: description.trim(),
      directToolUrl,
      documentationUrl,
      selectedCategories,
      selectedJobFamilies,
      toolMaturity,
      dataClassification,
      basePlatform,
      platformName,
      toolBindle,
      urlAnalysis: urlAnalysis || undefined,
      tags,
      basePlatformMetadataTaxonomyId,
    };

    // For custom built tools, go to the custom built details page
    if (shouldShowNextButton()) {
      props.onNext(formData);
    } else {
      if (props.isEdit) {
        if (!props.initialData?.taxonomyId) {
          addErrorBanner(getString("aiForMe.missingData"));
          return;
        }
        handleToolSubmission(formData, props.initialData.taxonomyId);
      } else {
        handleToolSubmission(formData);
      }
    }
  };

  const handleReset = (e: React.FormEvent<HTMLElement>) => {
    history.push(Paths.aiforme.url);
    scrollTo({
      top: 0,
    });
  };

  return (
    <GridContainer>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
      <GridItem lg={6} md={4}>
        <form onSubmit={handleSubmit} style={{ width: "100%" }} onReset={handleReset}>
          <FormWrapper width={"100%"}>
            <Col rowGap={"30px"}>
              <InputWrapper labelText={getString("aiForMe.formTitle")} id="title">
                {(inputProps) => (
                  <Input
                    {...inputProps}
                    data-cy="tool-title"
                    placeholder={getString("aiForMe.namePlaceholder")}
                    value={title}
                    disabled={props.isEdit}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                    onBlur={() => setTitle(title.trim())}
                    required
                    touchpoint="intake-ai-tool-name"
                  />
                )}
              </InputWrapper>

              <InputWrapper
                labelText={getString("aiForMe.formDescription")}
                id="description"
                shouldValidateNonInclusiveTerms={true}
              >
                {(inputProps) => (
                  <TextArea
                    placeholder={getString("aiForMe.descriptionPlaceholder")}
                    value={description}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                    onBlur={() => setDescription(description.trim())}
                    required
                    rows={5}
                    touchpoint="intake-ai-tool-description"
                    data-cy="tool-description"
                  />
                )}
              </InputWrapper>

              <InputWrapper
                labelText={getString("aiForMe.formDirectToolURL")}
                id="direct_tool"
                footer={urlAnalysis ? `URL recognized as ${urlAnalysis.platformName}` : undefined}
              >
                {(inputProps) => (
                  <Input
                    placeholder={getString("aiForMe.directToolURLPlaceholder")}
                    type="url"
                    required
                    touchpoint="intake-direct-tool-url"
                    data-cy="direct-tool-url"
                    value={directToolUrl}
                    onChange={handleDirectToolUrlChange}
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <InputWrapper labelText={getString("aiForMe.formJobFamilies")} id="job_families">
                {(inputProps) => (
                  <MultiSelect
                    data-cy="job-families-select"
                    columns={3}
                    options={jobFamilies}
                    value={selectedJobFamilies}
                    isMulti={true}
                    isLoading={loadingAIForMe}
                    onChange={(selected: string[]) => setSelectedJobFamilies(selected)}
                    placeholder={getString("aiForMe.selectJobFamilyPlaceholder")}
                    touchpoint="intake-job-families-multiselect"
                  />
                )}
              </InputWrapper>

              <InputWrapper labelText={getString("aiForMe.formCategories")} id="categories">
                {(inputProps) => (
                  <MultiSelect
                    data-cy="categories-select"
                    columns={3}
                    options={_.filter(categories, (s) => s !== "Recommended")}
                    value={selectedCategories}
                    isMulti={true}
                    isLoading={loadingAIForMe}
                    onChange={(selected: string[]) => setSelectedCategories(selected)}
                    placeholder={getString("aiForMe.selectCategoryPlaceholder")}
                    touchpoint="intake-categories-multiselect"
                  />
                )}
              </InputWrapper>

              {/* Only show Tool Maturity and Data Classification for non-custom built tools */}
              {platformName !== PlatformName.MODEL_HOSTING && platformName !== PlatformName.THIRD_PARTY && (
                <>
                  <InputWrapper labelText={getString("aiForMe.formToolMaturity")} id="tool-maturity">
                    {(inputProps) => (
                      <Select
                        options={TOOL_MATURITY_OPTIONS}
                        value={toolMaturity}
                        onChange={handleToolMaturityChange}
                        data-cy="intake-tool-maturity"
                        defaultValue={ToolMaturity.DEVELOPMENT}
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>

                  <InputWrapper
                    labelText={getString("aiForMe.formDataClassification")}
                    id="data-classification"
                    footer={
                      isDataClassificationDisabled()
                        ? getString("aiForMe.dataClassificationMessages.nonProductionTool", {
                            defaultPublicClassification,
                            toolMaturity,
                            productionMaturity: ToolMaturity.PRODUCTION,
                          })
                        : undefined
                    }
                  >
                    {(inputProps) => (
                      <Select
                        options={dataClassifications}
                        value={dataClassification}
                        onChange={(value: string) => setDataClassification(value)}
                        disabled={isDataClassificationDisabled()}
                        data-cy="intake-data-classification"
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>
                </>
              )}

              {(urlAnalysis?.platformName === PlatformName.MODEL_HOSTING ||
                urlAnalysis?.platformName === PlatformName.THIRD_PARTY) && (
                <InputWrapper labelText={getString("aiForMe.formDocumentationURL")} id="doc_url">
                  {(inputProps) => (
                    <Input
                      placeholder={getString("aiForMe.documentationURLPlaceholder")}
                      value={documentationUrl}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentationUrl(e.target.value)}
                      type="url"
                      touchpoint="intake-ai-tool-documentation-link"
                      data-cy="tool-documentation-link"
                    />
                  )}
                </InputWrapper>
              )}

              {showBindleInput() && (
                <InputWrapper
                  labelText={getString("aiForMe.intakeToolBindle")}
                  id="tool-bindle"
                  footer="Enter a valid bindle (e.g. amzn1.bindle.resource.abc123)"
                >
                  {(inputProps) => (
                    <Input
                      placeholder="Enter tool bindle"
                      pattern={BINDLE_PATTERN}
                      value={toolBindle}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToolBindle(e.target.value)}
                      data-cy="intake-tool-bindle"
                      touchpoint="tool-bindle-input"
                      {...inputProps}
                    />
                  )}
                </InputWrapper>
              )}

              <Row justifyContent="space-between" columnGap={5}>
                <Button
                  variant={ButtonVariant.Secondary}
                  disabled={isLoading}
                  type="reset"
                  touchpoint="intake-cancel-form"
                  data-cy="intake-cancel-button"
                >
                  {getString("aiForMe.cancel")}
                </Button>
                <Row columnGap={5}>
                  <Button
                    variant={ButtonVariant.Primary}
                    disabled={isLoading}
                    data-cy={shouldShowNextButton() ? "intake-next-button" : "intake-submit-tool-button"}
                    type="submit"
                    touchpoint={shouldShowNextButton() ? "intake-next" : "intake-submit-tool"}
                    customData={{
                      title,
                      type: "Tool",
                      description,
                      directToolUrl,
                      learnMoreLink: directToolUrl,
                      categories: selectedCategories,
                      jobFamilies: selectedJobFamilies,
                      status: "PENDING" as ApprovalStatus,
                      tags: [],
                      creationDate: new Date().toISOString(),
                    }}
                  >
                    {isLoading && <Spinner size={SpinnerSize.Small} />}
                    {!isLoading &&
                      (shouldShowNextButton() ? getString("aiForMe.nextPage") : getString("aiForMe.submitTool"))}
                  </Button>
                </Row>
              </Row>
            </Col>
          </FormWrapper>
        </form>
      </GridItem>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
    </GridContainer>
  );
};
